import { useEffect, useRef } from 'react';
import { MapPin } from 'lucide-react';
import { ApiError } from '@/lib/api';

/**
 * "We haven't launched here yet", at the moment it stops someone.
 *
 * This is the one place a customer outside the launch area is actually blocked — browsing
 * stays open on purpose, because the alternative is that anyone who declines the location
 * permission gets a wall instead of an app. So by the time this renders, the customer has
 * chosen a delivery address, filled a basket and pressed the button. A single line of red
 * text is the wrong weight for that: they need to know it is not their mistake, not a bug,
 * and not something retrying will fix.
 *
 * Two shapes, because the fix differs and the customer cannot be expected to work out which:
 *
 *   DELIVERY_OUT_OF_AREA — the ADDRESS is outside. Change the address, or wait for us.
 *   VENDOR_OUT_OF_AREA   — a SELLER is outside. Remove them; the rest of the basket is fine.
 *
 * The wording comes from the server (`details.body`) so it can be corrected without shipping
 * an app, and so the vendor console and the customer app never drift into telling different
 * stories about the same launch.
 */

const REGION_CODES = ['DELIVERY_OUT_OF_AREA', 'VENDOR_OUT_OF_AREA'] as const;

/** Narrow an unknown mutation error to a region block, or null if it is something else. */
export function as_region_block(err: unknown): ApiError | null {
  return err instanceof ApiError && (REGION_CODES as readonly string[]).includes(err.error_code) ? err : null;
}

export function RegionBlockCallout({ error }: { error: ApiError }) {
  const details = (error.details ?? {}) as { body?: string; vendor_names?: string[]; pincode?: string | null };
  const names = Array.isArray(details.vendor_names) ? details.vendor_names : [];
  const ref = useRef<HTMLDivElement>(null);

  /*
   * Bring it into view. This renders inside a scrolling sheet, directly above a button the
   * customer has just pressed — which is exactly where a tall basket or a small screen can
   * leave it below the fold. An unseen explanation reads as a dead button.
   */
  useEffect(() => {
    ref.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [error]);

  return (
    <div
      ref={ref}
      className="rounded-card border-2 border-warning/40 bg-warning/10 p-4"
      data-testid="region-block"
      data-error-code={error.error_code}
      /* Assertive rather than polite: this replaced the action the customer just took, so a
         screen-reader user should hear it now, not after whatever else is being announced. */
      role="alert"
    >
      <div className="flex items-start gap-3">
        <MapPin className="mt-0.5 h-5 w-5 shrink-0 text-warning" aria-hidden />
        <div className="min-w-0 space-y-1.5">
          <p className="font-heading font-bold" data-testid="region-block-title">
            {error.message}
          </p>
          {details.body && (
            <p className="text-sm text-muted-foreground" data-testid="region-block-body">
              {details.body}
            </p>
          )}
          {names.length > 0 && (
            /* Named, not counted. "2 sellers are unavailable" leaves the customer opening every
               line in the basket to find which two. */
            <ul className="mt-1 space-y-0.5 text-sm font-medium" data-testid="region-block-vendors">
              {names.map((n) => (
                <li key={n}>· {n}</li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
